import React, { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AdminDataContext } from "../../../useContext/AdminContext";
import moment from "moment";
import API from "../../../api";
import ProductDetailsModal from "../Modals/ProductDetailsModal";
import Grid from "@mui/material/Grid";
import "./Product.css";
import { Button } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
const Product = () => {
  const { snackbar, setIsLoading } = useContext(AdminDataContext);
  const location = useLocation();
  const navigate = useNavigate();

  // State management
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalProducts: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("date_created");
  const [sortOrder, setSortOrder] = useState("desc");

  // Filter states - Default productType to 'bundle'
  const [filters, setFilters] = useState({
    category: "",
    minPrice: "",
    maxPrice: "",
    isVisible: "",
    isFeatured: "",
    productType: "bundle", // Default to Bundle products only
    // category_id: "",
  });

  // UI states
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch products
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: limit.toString(),
        sortBy,
        sortOrder,
        ...(search && { search }),
        ...(filters.category && { category: filters.category }),
        ...(filters.category_id && { category_id: filters.category_id }),
        ...(filters.minPrice && { minPrice: filters.minPrice }),
        ...(filters.maxPrice && { maxPrice: filters.maxPrice }),
        ...(filters.isVisible !== "" && { isVisible: filters.isVisible }),
        ...(filters.isFeatured !== "" && { isFeatured: filters.isFeatured }),
        ...(filters.productType !== "" && { productType: filters.productType }),
      });

      const response = await API.get(`/api/v1/products/products?${params}`);

      if (response.data.success) {
        setProducts(response.data.data.products || []);
        setPagination(
          response.data.data.pagination || {
            currentPage: 1,
            totalPages: 1,
            totalProducts: 0,
            limit: 10,
            hasNextPage: false,
            hasPrevPage: false,
          }
        );
      } else {
        snackbar("Failed to fetch products", "error");
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      snackbar("Error fetching products", "error");
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories for filter
  const fetchCategories = async () => {
    try {
      const response = await API.get("/api/v1/category/categories");
      if (response.data.success) {
        // Handle the nested structure: response.data.data.categories
        const categoriesData =
          response.data.data?.categories || response.data.data || [];
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]); // Ensure categories is always an array
    }
  };

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (page === 0) {
        fetchProducts();
      } else {
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [search]);

  // Handle page change
  useEffect(() => {
    fetchProducts();
  }, [page, limit, sortBy, sortOrder, filters]);

  // Initial load and refresh on navigation
  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [location.pathname, location.search]);

  // Event handlers
  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setSelectedProductId(product.id);
    setViewModalOpen(true);
  };

  const handleEditProduct = (product) => {
    // Navigate to edit product page without page refresh
    navigate(`/products/edit/${product.id}`);
  };

  const handleDeleteProduct = (product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      setDeleteLoading(true);
      const response = await API.delete(
        `/api/v1/products/delete-product/${productToDelete.id}`
      );

      if (response.data.success) {
        snackbar("Product deleted successfully", "success");
        setDeleteDialogOpen(false);
        setProductToDelete(null);
        // Refresh the product list
        fetchProducts();
      } else {
        snackbar(response.data.message || "Failed to delete product", "error");
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      const errorMessage =
        error.response?.data?.message || "Error deleting product";
      snackbar(errorMessage, "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  const handleAddProduct = () => {
    // Navigate to create product page without page  refresh
    navigate("/products/create");
  };

  const handleRefresh = () => {
    fetchProducts();
  };

  const handleClearFilters = () => {
    setFilters({
      category: "",
      minPrice: "",
      maxPrice: "",
      isVisible: "",
      isFeatured: "",
      productType: "bundle", // Keep Bundle filter
    });
    setSearch("");
  };

  const handleFilterChange = (field, value) => {
    console.log("field value", field, value);

    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  return (
    <div className="product-container">
      {/* Header */}
      <div className="product-header">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8} md={8}>
            <h1 className="bc-products-title">Bundle Management</h1>
            <p className="bc-products-subtitle">
              Manage your Bundle catalog with advanced search and filtering
            </p>
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            md={4}
            sx={{
              display: "flex",
              justifyContent: { xs: "start", sm: "flex-end" },
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddProduct}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                background: "linear-gradient(135deg, #42A5F5 0%, #1565C0 100%)",
                boxShadow: "0 4px 15px rgba(66, 165, 245, 0.4)",
                transition: "all 0.3s ease",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)",
                  boxShadow: "0 6px 20px rgba(66, 165, 245, 0.6)",
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Add Bundle
            </Button>
          </Grid>
        </Grid>
      </div>

      {/* Search and Filter Bar */}
      <div className="search-filter-bar">
        <div className="filter-row">
          {/* Search Field */}
          <div className="search-input-wrapper">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              className="search-input"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button
                className="clear-icon"
                onClick={() => setSearch("")}
                aria-label="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          {/* Sort By */}
          <div className="form-control">
            <select
              className="form-select"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="date_created">Sort By: Date Created</option>
              {/* <option value="name">Sort By: Name</option> */}
              <option value="calculated_price">Sort By: Price</option>
              <option value="inventory_level">Sort By: Stock</option>
            </select>
          </div>

          {/* Sort Order */}
          <div className="form-control">
            <select
              className="form-select"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <option value="desc">Order: Descending</option>
              <option value="asc">Order: Ascending</option>
            </select>
          </div>

          {/* Action Buttons */}
          <div className="button-group">
            <button className="btn" onClick={() => setFilterOpen(true)}>
              <span>📋</span>
              Filters
            </button>
            <button className="btn" onClick={handleRefresh}>
              <span>🔄</span>
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {(Object.values(filters).some((v) => v !== "" && v !== "bundle") ||
        search) && (
        <div className="active-filters">
          <h3 className="active-filters-title">Active Filters:</h3>
          <div className="chips-container">
            {search && (
              <div className="chip chip-primary">
                Search: "{search}"
                <button
                  className="chip-delete"
                  onClick={() => setSearch("")}
                  aria-label="Remove search filter"
                >
                  ✕
                </button>
              </div>
            )}
            {filters.category && (
              <div className="chip chip-secondary">
                Category: {filters.category}
                <button
                  className="chip-delete"
                  onClick={() => handleFilterChange("category", "")}
                  aria-label="Remove category filter"
                >
                  ✕
                </button>
              </div>
            )}
            {filters.minPrice && (
              <div className="chip chip-info">
                Min: ${filters.minPrice}
                <button
                  className="chip-delete"
                  onClick={() => handleFilterChange("minPrice", "")}
                  aria-label="Remove min price filter"
                >
                  ✕
                </button>
              </div>
            )}
            {filters.maxPrice && (
              <div className="chip chip-info">
                Max: ${filters.maxPrice}
                <button
                  className="chip-delete"
                  onClick={() => handleFilterChange("maxPrice", "")}
                  aria-label="Remove max price filter"
                >
                  ✕
                </button>
              </div>
            )}
            {filters.isVisible !== "" && (
              <div className="chip chip-success">
                Visible: {filters.isVisible ? "Yes" : "No"}
                <button
                  className="chip-delete"
                  onClick={() => handleFilterChange("isVisible", "")}
                  aria-label="Remove visibility filter"
                >
                  ✕
                </button>
              </div>
            )}
            {filters.isFeatured !== "" && (
              <div className="chip chip-warning">
                Featured: {filters.isFeatured ? "Yes" : "No"}
                <button
                  className="chip-delete"
                  onClick={() => handleFilterChange("isFeatured", "")}
                  aria-label="Remove featured filter"
                >
                  ✕
                </button>
              </div>
            )}
            {filters.productType !== "" && filters.productType !== "bundle" && (
              <div className="chip chip-info">
                Product Type: {filters.productType}
                <button
                  className="chip-delete"
                  onClick={() => handleFilterChange("productType", "bundle")}
                  aria-label="Remove product type filter"
                >
                  ✕
                </button>
              </div>
            )}
            <button className="btn btn-small" onClick={handleClearFilters}>
              ✕ Clear All
            </button>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            <h3 className="empty-state-title">No products found</h3>
            <p className="empty-state-text">
              Try adjusting your search or filters
            </p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="products-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Product Name</th>
                  <th>Price</th>
                  <th>Sale Price</th>
                  <th>Cost</th>
                  <th>Retail</th>
                  <th>Stock</th>
                  <th>Tracking</th>
                  <th>Type</th>
                  <th>Visible</th>
                  <th>Featured</th>
                  <th>Sold</th>
                  <th>Custom</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => {
                  const dynamicStock = product.inventory_level || 0;
                  const isCustom =
                    product.bCProductCustomField?.is_custom_product;
                  const hasCustomField = !!product.bCProductCustomField;

                  return (
                    <tr key={product._id || product.id}>
                      <td>{product.id}</td>
                      <td>
                        <div className="product-name-cell">
                          <div
                            className="product-name tooltip"
                            title={product.name}
                          >
                            {product.name}
                            <span className="tooltiptext">{product.name}</span>
                          </div>
                          <div className="product-sku">
                            SKU: {product.sku || "N/A"}
                          </div>
                        </div>
                      </td>
                      <td>
                        {product.sale_price > 0 ? (
                          <div className="price-cell">
                            <div className="price-original">
                              ${product.price?.toFixed(2) || "0.00"}
                            </div>
                            <div className="price-sale">
                              ${product.sale_price?.toFixed(2) || "0.00"}
                            </div>
                          </div>
                        ) : (
                          <div className="price-regular">
                            ${product.price?.toFixed(2) || "0.00"}
                          </div>
                        )}
                      </td>
                      <td>
                        {product.sale_price > 0 ? (
                          <span className="price-sale">
                            ${product.sale_price?.toFixed(2)}
                          </span>
                        ) : (
                          <span className="price-no-sale">No Sale</span>
                        )}
                      </td>
                      <td>${product.cost_price?.toFixed(2) || "0.00"}</td>
                      <td>${product.retail_price?.toFixed(2) || "0.00"}</td>
                      <td>
                        <span
                          className={`stock-badge ${
                            dynamicStock > 0 ? "stock-in" : "stock-out"
                          }`}
                        >
                          {dynamicStock}
                        </span>
                      </td>
                      <td className="text-secondary">
                        {product.inventory_tracking || "-"}
                      </td>
                      <td className="text-secondary">
                        {product.bCProductCustomField?.is_bundle
                          ? "Bundle"
                          : "-"}
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            product.is_visible ? "badge-success" : "badge-error"
                          }`}
                        >
                          {product.is_visible ? "Enabled" : "Disabled"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            product.is_featured
                              ? "badge-warning"
                              : "badge-default"
                          }`}
                        >
                          {product.is_featured ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="text-secondary">
                        {product.total_sold || 0}
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            isCustom ? "badge-info" : "badge-default"
                          }`}
                        >
                          {isCustom ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="date-text">
                        {moment(product.date_created).format("MMM DD, YYYY")}
                      </td>
                      <td>
                        <div className="actions-cell">
                          <button
                            className="btn-icon"
                            onClick={() => handleViewProduct(product)}
                            aria-label="View product"
                            title="View details"
                          >
                            👁️
                          </button>
                          <button
                            className="btn-icon"
                            onClick={() => handleEditProduct(product)}
                            disabled={!hasCustomField}
                            aria-label="Edit product"
                            title="Edit product"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-icon"
                            onClick={() => handleDeleteProduct(product)}
                            disabled={!hasCustomField}
                            aria-label="Delete product"
                            title="Delete product"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination Info */}
      <div className="pagination-info">
        <div className="pagination-text">
          Showing {page * limit + 1} to{" "}
          {Math.min((page + 1) * limit, pagination.totalProducts)} of{" "}
          {pagination.totalProducts} products
        </div>
        <div className="pagination-controls">
          <button
            className="btn btn-small"
            onClick={() => setPage(0)}
            disabled={page === 0}
          >
            First
          </button>
          <button
            className="btn btn-small"
            onClick={() => setPage(page - 1)}
            disabled={page === 0}
          >
            Previous
          </button>
          <span className="pagination-text">
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <button
            className="btn btn-small"
            onClick={() => setPage(page + 1)}
            disabled={page >= pagination.totalPages - 1}
          >
            Next
          </button>
          <button
            className="btn btn-small"
            onClick={() => setPage(pagination.totalPages - 1)}
            disabled={page >= pagination.totalPages - 1}
          >
            Last
          </button>
          <select
            className="pagination-select"
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(0);
            }}
          >
            <option value="5">5 per page</option>
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
          </select>
        </div>
      </div>

      {/* Filter Dialog */}
      {filterOpen && (
        <div className="modal-overlay" onClick={() => setFilterOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Filters</h2>
              <button
                className="modal-close"
                onClick={() => setFilterOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="modal-content">
              <div className="filter-grid">
                {/* Category Filter */}
                <div className="form-control">
                  <label className="form-label">Category</label>
                  <select
                    className="form-select"
                    value={filters.category}
                    onChange={(e) =>
                      handleFilterChange("category", e.target.value)
                    }
                  >
                    <option value="">All Categories</option>
                    {Array.isArray(categories) &&
                      categories.map((category) => (
                        <option key={category._id} value={category.category_id}>
                          {category.name}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Price Range */}
                <div className="filter-grid-2col">
                  <div className="form-control">
                    <label className="form-label">Min Price</label>
                    <input
                      type="number"
                      className="search-input"
                      placeholder="$"
                      value={filters.minPrice}
                      onChange={(e) =>
                        handleFilterChange("minPrice", e.target.value)
                      }
                    />
                  </div>

                  <div className="form-control">
                    <label className="form-label">Max Price</label>
                    <input
                      type="number"
                      className="search-input"
                      placeholder="$"
                      value={filters.maxPrice}
                      onChange={(e) =>
                        handleFilterChange("maxPrice", e.target.value)
                      }
                    />
                  </div>
                </div>

                {/* Visibility Filter */}
                <div className="form-control">
                  <label className="form-label">Visibility</label>
                  <select
                    className="form-select"
                    value={filters.isVisible}
                    onChange={(e) =>
                      handleFilterChange("isVisible", e.target.value)
                    }
                  >
                    <option value="">All</option>
                    <option value="true">Visible</option>
                    <option value="false">Hidden</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={handleClearFilters}>
                Clear All
              </button>
              <button
                className="btn btn-primary"
                onClick={() => setFilterOpen(false)}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Details Modal */}
      <ProductDetailsModal
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        productId={selectedProductId}
      />

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <div className="modal-overlay" onClick={cancelDelete}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-error">
              <span>🗑️</span>
              Confirm Product Deletion
            </div>
            <div className="modal-content">
              <div className="alert alert-warning">
                <span className="alert-icon">⚠️</span>
                <div>
                  This action cannot be undone. The product will be permanently
                  deleted.
                </div>
              </div>
              {productToDelete && (
                <div>
                  <p>Are you sure you want to delete this product?</p>
                  <div className="product-details-box">
                    <h3 className="product-detail-title">
                      {productToDelete.name}
                    </h3>
                    <p className="product-detail-text">
                      SKU: {productToDelete.sku || "N/A"}
                    </p>
                    <p className="product-detail-text">
                      Price: $
                      {productToDelete.calculated_price?.toFixed(2) || "0.00"}
                    </p>
                    <p className="product-detail-text">
                      Stock: {productToDelete.inventory_level || 0}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button
                className="btn"
                onClick={cancelDelete}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-error"
                onClick={confirmDeleteProduct}
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <>
                    <span className="spinner spinner-small"></span>
                    Deleting...
                  </>
                ) : (
                  <>
                    <span>🗑️</span>
                    Delete Product
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Product;
